import {inject, injectable} from 'inversify';

import type {MembershipApiModel} from '../../../../api/membership';
import type {IApiDataMapper} from '../../../ports/types';
import type {MembershipModel} from '../Membership.model';

import {PrivateProfileMapperJrpc} from '../../common/PrivateProfile/mappers/PrivateProfile.mapper.jrpc';
import {CourseMapperJrpc} from '../../Course/mappers/Course.mapper.jrpc';
import {SubtenantMapperJrpc} from '../../Subtenant/mappers/Subtenant.mapper.jrpc';
import {MembershipFactory} from '../Membership.factory';
import {MembershipExpandJrpc} from './Membership.expand.jrpc';

@injectable()
export class MembershipMapperJrpc
  implements
    IApiDataMapper<
      MembershipMapperJrpc.ApiData,
      MembershipModel.Props,
      MembershipModel
    >
{
  constructor(
    @inject(MembershipFactory)
    private readonly factory: MembershipFactory,
    @inject(MembershipExpandJrpc) public readonly expand: MembershipExpandJrpc,
    @inject(CourseMapperJrpc) private readonly courseMapper: CourseMapperJrpc,
    @inject(PrivateProfileMapperJrpc)
    private readonly privateProfileMapper: PrivateProfileMapperJrpc,
    @inject(SubtenantMapperJrpc)
    private readonly subtenantMapper: SubtenantMapperJrpc,
  ) {}

  mapDataToModel(
    apiData: MembershipMapperJrpc.ExpandedApiData,
  ): MembershipModel {
    const props = this.mapDataToProps(apiData);

    return this.factory.create(props);
  }

  mapDataToProps(
    apiData: MembershipMapperJrpc.ExpandedApiData,
  ): MembershipModel.Props {
    return {
      cardId: this.cardId(apiData),
      course: this.course(apiData),
      id: apiData._id,
      privateProfile: this.privateProfile(apiData),
      status: this.status(apiData),
      subtenant: this.subtenant(apiData),
    };
  }

  private cardId(
    apiData: MembershipMapperJrpc.ApiData,
  ): MembershipModel.Props['cardId'] {
    return apiData.card_id;
  }

  private course(
    apiData: MembershipMapperJrpc.ExpandedApiData,
  ): MembershipModel.Props['course'] {
    if (!apiData.course) {
      return null;
    }

    return this.courseMapper.mapDataToModel(apiData.course);
  }

  private privateProfile(
    apiData: MembershipMapperJrpc.ApiData,
  ): MembershipModel.Props['privateProfile'] {
    const privateProfileData = apiData.private_profile ?? {};

    return this.privateProfileMapper.mapDataToModel(privateProfileData);
  }

  private status(
    apiData: MembershipMapperJrpc.ApiData,
  ): MembershipModel.Props['status'] {
    return apiData.status;
  }

  private subtenant(
    apiData: MembershipMapperJrpc.ExpandedApiData,
  ): MembershipModel.Props['subtenant'] {
    if (!apiData.subtenant) {
      return null;
    }

    return this.subtenantMapper.mapExpandedToModel(apiData.subtenant);
  }
}

export namespace MembershipMapperJrpc {
  export type ApiData = MembershipApiModel;

  export type ExpandedApiData = ApiData & MembershipExpandJrpc.ExpandedFields;
}
